import { Currency, PaymentMethodModificationRequest } from '@nemuru-eng/js-api-client';
import { useCallback, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { AutomobileFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/types/AutomobileFormItem';
import {
  EditAutoPaymentMethodFinancingFields,
  EditAutoPaymentMethodFormFields,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/schemas/editAutoPaymentMethodForm.schema';
import { EditAutoPaymentMethodDialogStep } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/types/EditAutoPaymentMethodDialogStep';
import { FinancingLiveSummaryData } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/types/FinancingLiveSummaryData';
import {
  buildAutoPaymentMethodModificationBody,
  PaymentMethodModificationOptions,
  toModificationPreviewRequest,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/maps/vehicleModificationPreview.map';
import { usePatchPaymentMethodModification } from '@/hooks/requests/checkoutSession/usePatchPaymentMethodModification';
import { usePostPaymentMethodModificationPreview } from '@/hooks/requests/checkoutSession/usePostPaymentMethodModificationPreview';

const DELAYED_LENDER_REQUEST_TIMEOUT = 6000;

interface UseEditAutoPaymentMethodMutationParams {
  checkoutSessionId: string;
  currency: Currency;
  initialFormItem: AutomobileFormItem;
  initialFinancing: FinancingLiveSummaryData;
  paymentMethodId: string | undefined;
  currentPaymentMethodOptions: PaymentMethodModificationOptions | undefined;
  selectedPaymentMethodOptions: PaymentMethodModificationOptions | undefined;
  goToStep: (step: EditAutoPaymentMethodDialogStep) => void;
  onNext: () => void;
  refreshCheckoutSessionAndPaymentMethodEntities: () => Promise<void>;
}

export const useEditAutoPaymentMethodMutation = ({
  checkoutSessionId,
  currency,
  initialFormItem,
  initialFinancing,
  paymentMethodId,
  currentPaymentMethodOptions,
  selectedPaymentMethodOptions,
  goToStep,
  onNext,
  refreshCheckoutSessionAndPaymentMethodEntities,
}: UseEditAutoPaymentMethodMutationParams) => {
  const { handleSubmit } = useFormContext<EditAutoPaymentMethodFormFields>();

  const [reviewItem, setReviewItem] = useState<AutomobileFormItem | undefined>(undefined);
  const [reviewFinancing, setReviewFinancing] = useState<EditAutoPaymentMethodFinancingFields | undefined>(undefined);
  const [modificationBody, setModificationBody] = useState<PaymentMethodModificationRequest | undefined>(undefined);
  const [showRequestDelayWarning, setShowRequestDelayWarning] = useState(false);

  const {
    error: previewError,
    loading: loadingPreview,
    paymentMethodModificationPreview,
    reset: resetPreview,
    submit: submitPreview,
  } = usePostPaymentMethodModificationPreview();
  const { loading: loadingApply, reset: resetApply, submit: submitApply } = usePatchPaymentMethodModification();

  const delayWarningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isApplyInFlightRef = useRef(false);

  const clearDelayWarningTimeout = useCallback(() => {
    if (delayWarningTimeoutRef.current) {
      clearTimeout(delayWarningTimeoutRef.current);
      delayWarningTimeoutRef.current = null;
    }
  }, []);

  const startDelayWarningTimeout = useCallback(() => {
    clearDelayWarningTimeout();
    delayWarningTimeoutRef.current = setTimeout(() => {
      if (isApplyInFlightRef.current) {
        setShowRequestDelayWarning(true);
      }
    }, DELAYED_LENDER_REQUEST_TIMEOUT);
  }, [clearDelayWarningTimeout]);

  const onPreview = handleSubmit(async ({ item, financing }) => {
    if (!paymentMethodId) {
      return;
    }

    const nextItem = item as AutomobileFormItem;
    const nextFinancing = financing as EditAutoPaymentMethodFinancingFields;
    resetPreview();
    resetApply();

    const nextModificationBody = buildAutoPaymentMethodModificationBody(
      initialFormItem,
      nextItem,
      currency,
      initialFinancing,
      nextFinancing,
      currentPaymentMethodOptions,
      selectedPaymentMethodOptions,
    );
    const previewRequest = toModificationPreviewRequest(nextModificationBody);

    if (previewRequest.changes || previewRequest.paymentMethodOptions) {
      await submitPreview({
        checkoutSessionId,
        paymentMethodId,
        body: previewRequest,
      });
    }

    setReviewItem(nextItem);
    setReviewFinancing(nextFinancing);
    setModificationBody(nextModificationBody);
    goToStep(EditAutoPaymentMethodDialogStep.CHANGES_REVIEW);
  });

  const onApply = async () => {
    if (!reviewItem || !reviewFinancing || !paymentMethodId || !modificationBody) {
      return;
    }

    isApplyInFlightRef.current = true;
    startDelayWarningTimeout();
    resetApply();

    try {
      await submitApply({
        checkoutSessionId,
        paymentMethodId,
        body: modificationBody,
      });

      refreshCheckoutSessionAndPaymentMethodEntities();
      onNext();
    } catch {
    } finally {
      isApplyInFlightRef.current = false;
      clearDelayWarningTimeout();
    }
  };

  return {
    reviewItem,
    reviewFinancing,
    modificationBody,
    previewError,
    loadingPreview,
    paymentMethodModificationPreview,
    loadingApply,
    showRequestDelayWarning,
    setShowRequestDelayWarning,
    clearDelayWarningTimeout,
    resetApply,
    onPreview,
    onApply,
  };
};
